/**
 * The tuner's control surface: the seven constants of the candidate scenario,
 * and what they currently add up to.
 *
 * The constants themselves do **not** live here. Each equation's group is
 * exported and rendered in the right rail of the chart it draws — the curve, the
 * (i) that explains it and the knob that bends it are then one thing to read,
 * and a change lands next to the line it moves. What is left in the panel is the
 * part that is about all of them at once: the current formula, Reset, and the
 * table of what the whole set adds up to.
 *
 * Each field is a plain number input, not a slider. The values being chosen are
 * round numbers that get typed and compared ("is it 25 or 30?"), and a slider
 * makes a specific number the hard case; the arrow keys still step.
 */

import {
  DEFAULT_SCENARIO_PARAMS,
  EVO_POWER_MAX,
  EVO_POWER_MIN,
  EVO_POWER_STEP,
  PASSIVE_POWER_STEP,
  effectivePassivePower,
  formatCompact,
  isDefaultScenario,
  marginalRatioMinutes,
  passivePowerBounds,
  scenarioMutagenForEvo,
  scenarioPassivePerSecond,
  toDisplayMutagen,
  type ScenarioParams,
} from '../model';
import { Info } from './Info';
import { useLab } from './LabContext';
import { NumberField } from './controls';

/** Evolutions the summary quotes at — the span the tuning is argued over. */
const QUOTE_AT = [0.5, 1, 1.5, 2.5];

/**
 * Every constant back to its default.
 *
 * Sits at the foot of the last chart's right rail, which puts it in the bottom
 * right corner of the chart panel — the far corner from the zoom overlay, below
 * the four rails it undoes rather than inside any one of them. It is named for
 * what it resets because it is now next to a *zoom* reset, and "Reset" alone
 * would be two different buttons with one word.
 */
export function ScenarioReset() {
  const { scenario, resetScenario } = useLab();
  const isDefault = isDefaultScenario(scenario);
  return (
    <button
      type="button"
      className="scenario-reset"
      onClick={resetScenario}
      disabled={isDefault}
      title={isDefault ? 'Already at the defaults' : 'Every constant back to its default'}
    >
      Reset scenario
    </button>
  );
}

export function ScenarioPanel() {
  const { scenario, scenarioRuleset, playerCount } = useLab();

  const n = effectivePassivePower(scenario);

  return (
    <section className="scenario-panel" aria-label="Scenario summary">
      <div className="scenario-head">
        <h2>Scenario</h2>
        <Info title="Tuning the scenario">
          <p>
            The three equations of the balance pass, with every constant on a control. The{' '}
            <b>Scenario</b> curve on each chart is this, drawn against the two transcribed branches
            over the same mutagen axis.
          </p>
          <p>
            <b>The controls are in the right rail of each chart</b>, beside the curve they move:
            evolution on row 1, instant threat on row 2, passive threat on row 3. This panel sits
            below the charts and keeps only what is about all of them at once; <b>Reset</b> is in
            the header, within reach of every rail.
          </p>
          <p>
            Two of them are bounded rather than free. <code>p</code> is the diminishing return
            itself, so it lives strictly inside <code>(0, 1)</code> — at 1 evolution is linear in
            mutagen and above it every flask buys more than the last. <code>n</code> lives strictly
            inside <code>(1, 1/p)</code>: under 1 the powered term bends downward and{' '}
            <code>b·E</code> is left doing all the work, and at <code>1/p</code> the exponent in
            mutagen reaches 1, which is where passive income stops diminishing in what has been fed.
            Its window therefore moves when <code>p</code> does, and raising <code>p</code> far
            enough pulls <code>n</code> down with it.
          </p>
          <p className="formula">
            E = (M / S) ^ p · P = a·E^n + b·E per second · T = c·M
          </p>
          <p>
            <b>Stop scaling at 100%</b> decides what evolution does past 100%. On, the power law is
            replaced by its own tangent, so an evolution point costs a flat <code>S / p</code>{' '}
            mutagen forever and the join has no kink. Off, the power law simply runs on, charging
            more per point without limit.
          </p>
          <p>
            Switching it off also <b>divides the passive exponent by p</b>. Evolution then stays{' '}
            <code>(M/S)^p</code> forever, so <code>E^n</code> would grow like <code>M^(n·p)</code> —
            with the defaults that is <code>M^0.75</code>, income per flask <i>falling away</i>{' '}
            rather than climbing. Dividing cancels it exactly: <code>E^(n/p) ≡ (M/S)^n</code>, so
            "n = 2.5" means the same thing to a player under either switch position.
          </p>
          <p>
            Everything here is in the address bar as <code>?tune=1&amp;sc=…</code>, so a candidate
            is one paste. Defaults are left out of the link deliberately — a bare{' '}
            <code>?tune=1</code> always opens whatever the defaults are <i>now</i>.
          </p>
        </Info>
        <span className="scenario-formula formula">
          E = (M/{fmt(scenario.evoMutagenAt100)})^{fmt(scenario.evoPower)}
          {scenario.evoStopScalingAt100 ? ', tangent past 100%' : ', power law throughout'}
          {' · '}P = {fmt(scenario.passiveScale)}·E^{fmt(n, 3)} + {fmt(scenario.passiveLinear)}·E
          {' · '}T = {fmt(scenario.instantScale)}·M
        </span>
      </div>

      <table className="scenario-table">
        <thead>
          <tr>
            <th>evo</th>
            <th>mutagen</th>
            <th>passive / min</th>
            <th>instant to reach</th>
            <th>instant ÷ passive</th>
          </tr>
        </thead>
        <tbody>
          {QUOTE_AT.map((evo) => {
            const mutagen = scenarioMutagenForEvo(evo, scenario);
            return (
              <tr key={evo}>
                <td>{(evo * 100).toFixed(0)}%</td>
                <td>{formatCompact(toDisplayMutagen(mutagen), 1)}</td>
                <td>{formatCompact(scenarioPassivePerSecond(evo, scenario) * 60, 1)}</td>
                <td>{formatCompact(scenario.instantScale * mutagen, 1)}</td>
                <td className="scenario-ratio">
                  {marginalRatioMinutes(scenarioRuleset, evo, playerCount).toFixed(2)} min
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

/* ------------------------------------------------------- the per-chart rails */

/** Chart 1's rail: what a point of evolution costs, and what happens past 100%. */
export function EvolutionParams() {
  const { scenario, setScenario } = useLab();
  return (
    <ParamGroup hint="E = (M/S)^p">
      <ParamField
        label="Mutagen at 100%"
        hint="S"
        value={scenario.evoMutagenAt100}
        min={1}
        max={100000}
        step={1}
        onChange={(evoMutagenAt100) => setScenario({ evoMutagenAt100 })}
      />
      <ParamField
        label="Scaling factor"
        hint="p"
        value={scenario.evoPower}
        min={EVO_POWER_MIN}
        max={EVO_POWER_MAX}
        step={EVO_POWER_STEP}
        range="0 < p < 1"
        onChange={(evoPower) => setScenario({ evoPower })}
      />
      <label className="scenario-check">
        <input
          type="checkbox"
          checked={scenario.evoStopScalingAt100}
          onChange={(event) => setScenario({ evoStopScalingAt100: event.target.checked })}
        />
        <span>
          Stop scaling at 100%
          <i>
            {scenario.evoStopScalingAt100
              ? `flat ${fmt(scenario.evoMutagenAt100 / scenario.evoPower, 0)} mutagen per +100% above`
              : 'power law runs on — passive exponent divided by p'}
          </i>
        </span>
      </label>
    </ParamGroup>
  );
}

/** Chart 2's rail. One constant, because the curve is a straight line. */
export function InstantThreatParams() {
  const { scenario, setScenario } = useLab();
  return (
    <ParamGroup hint="T = c·M">
      <ParamField
        label="Scale"
        hint="c"
        value={scenario.instantScale}
        min={0}
        max={100000}
        step={5}
        onChange={(instantScale) => setScenario({ instantScale })}
      />
      <p className="scenario-note">
        Flat in player count — the crowd modifier <code>min(125 + 6.25P, 250)</code> is not applied
        here.
      </p>
    </ParamGroup>
  );
}

/**
 * Chart 3's rail. `n`'s bounds are read off `p` on every render rather than
 * stored, so the field cannot disagree with the evolution curve it depends on.
 */
export function PassiveThreatParams() {
  const { scenario, setScenario } = useLab();
  const bounds = passivePowerBounds(scenario.evoPower);
  const corrected = !scenario.evoStopScalingAt100;
  return (
    <ParamGroup hint="P = a·E^n + b·E">
      <ParamField
        label="Scaling constant"
        hint="a"
        value={scenario.passiveScale}
        min={0}
        max={100000}
        step={1}
        onChange={(passiveScale) => setScenario({ passiveScale })}
      />
      <ParamField
        label="Scaling factor"
        hint="n"
        value={scenario.passivePower}
        min={bounds.min}
        max={bounds.max}
        step={PASSIVE_POWER_STEP}
        range={`1 < n < ${fmt(1 / scenario.evoPower, 2)}`}
        note={corrected ? `runs at ${fmt(effectivePassivePower(scenario), 3)}` : undefined}
        onChange={(passivePower) => setScenario({ passivePower })}
      />
      <ParamField
        label="Linear constant"
        hint="b"
        value={scenario.passiveLinear}
        min={0}
        max={100000}
        step={1}
        onChange={(passiveLinear) => setScenario({ passiveLinear })}
      />
    </ParamGroup>
  );
}

/* -------------------------------------------------------------------------- */

/** Trim to the shortest form that still reads exactly. */
function fmt(value: number, digits = 3): string {
  if (!Number.isFinite(value)) return '—';
  return Number(value.toFixed(digits)).toString();
}

/**
 * The group is titled by its equation and not by its subject: the chart it sits
 * on is already called "Evolution", and "Scenario" is the part that is not
 * obvious — while comparing, these knobs move one of the drawn curves and not
 * the other.
 */
function ParamGroup({ hint, children }: { hint: string; children: React.ReactNode }) {
  return (
    <div className="scenario-group">
      <span className="scenario-group-head">
        <b>Scenario</b>
        <code className="formula">{hint}</code>
      </span>
      {children}
    </div>
  );
}

function ParamField({
  label,
  hint,
  value,
  min,
  max,
  step,
  range,
  note,
  onChange,
}: {
  label: string;
  /** The symbol this field is, in the formula above. */
  hint: string;
  value: number;
  min: number;
  max: number;
  step: number;
  /** The interval the value is defined on, for a field that has one. */
  range?: string;
  /** What the value turns into, when that is not the value itself. */
  note?: string;
  onChange: (value: number) => void;
}) {
  const defaultValue = DEFAULT_SCENARIO_PARAMS[fieldOf(hint)];
  const moved = typeof defaultValue === 'number' && defaultValue !== value;
  return (
    <label className={`scenario-field${moved ? ' moved' : ''}`}>
      <span className="scenario-field-label">
        {label} <code>{hint}</code>
      </span>
      <NumberField value={value} onChange={onChange} min={min} max={max} step={step} label={label} />
      {range ? <i className="scenario-field-range">{range}</i> : null}
      {note ? <i className="scenario-field-note">{note}</i> : null}
    </label>
  );
}

/** Symbol to parameter, so a field can tell whether it has been moved. */
const FIELD_BY_SYMBOL: Record<string, keyof ScenarioParams> = {
  S: 'evoMutagenAt100',
  p: 'evoPower',
  a: 'passiveScale',
  n: 'passivePower',
  b: 'passiveLinear',
  c: 'instantScale',
};

const fieldOf = (symbol: string): keyof ScenarioParams =>
  FIELD_BY_SYMBOL[symbol] ?? 'evoMutagenAt100';
